import React, { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'react-query';
import IconButton from 'material-ui/IconButton';
import LinearProgress from 'material-ui/LinearProgress';
import MenuItem from 'material-ui/MenuItem';
import SelectField from 'material-ui/SelectField';
import NavigationClose from 'material-ui/svg-icons/navigation/close';
import { Tab, Tabs } from 'material-ui/Tabs';
import { getSuiteDoc, listRunsForDoc } from 'services/testRuns';
import { DocStatus, HoverTip, LetterLink } from './HoverTip';
import JsonDiff from './JsonDiff';
import JapeRulesTable from './JapeRulesTable';
import { formatBytes, formatCompare, formatMetric, formatSeconds, formatSuiteLabel, runSelectionTip } from './metrics';
import './styles.scss';

function japeRows(output) {
    const stats = (output && output.executionStats) || {};
    const jape = stats.japeMetrics || {};
    const rows = [];
    Object.keys(jape).forEach((docName) => {
        const phases = (jape[docName] && jape[docName].phases) || [];
        phases.forEach((phase) => {
            const rules = phase.rules || {};
            Object.keys(rules).forEach((rule) => {
                rows.push({
                    docName,
                    phase: phase.phase,
                    rule,
                    lhsCandidates: rules[rule].lhsCandidates,
                    rhsFirings: rules[rule].rhsFirings,
                    annotationsCreated: rules[rule].annotationsCreated,
                });
            });
        });
    });
    return rows;
}

function recsByType(data) {
    const map = new Map();
    ((data && data.recognitions) || []).forEach((rec) => {
        map.set(rec.annotationType, rec);
    });
    return map;
}

function outputOf(rec) {
    if (!rec || rec.state !== 'done') {
        return null;
    }
    return rec.output || {};
}

function statsOf(output) {
    const stats = (output && output.executionStats) || {};
    const resource = stats.resourceStats || (output && output.resourceStats) || {};
    const counter = (output && output.resultCounter) || {};
    const rows = japeRows(output);
    const sum = (field) => rows.reduce((total, row) => (
        typeof row[field] === 'number' ? total + row[field] : total
    ), 0);
    const has = (field) => rows.some(row => typeof row[field] === 'number');
    return {
        time: stats.timeSpent,
        heap: resource.heapUsedAfterJobGcBytes,
        matched: stats.totalMatchedAnnotations,
        annotations: typeof counter.annotations === 'number' ? counter.annotations : (has('annotationsCreated') ? sum('annotationsCreated') : undefined),
        lhs: has('lhsCandidates') ? sum('lhsCandidates') : undefined,
        rhs: has('rhsFirings') ? sum('rhsFirings') : undefined,
        stack: stats.clarifyProfileInfo,
    };
}

function recognitionStatus(rec) {
    if (!rec || rec.state === 'pending') {
        return 'pending';
    }
    if (rec.routing === 'completed') {
        return 'done';
    }
    return 'failed';
}

function clarifiedOf(output) {
    if (!output || !Object.prototype.hasOwnProperty.call(output, 'newclarified')) {
        return undefined;
    }
    return output.newclarified;
}

function textField(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function recognitionFailure(output, rec) {
    const routing = rec && rec.routing;
    const status = output && output.status;
    const failed = routing === 'failed' || routing === 'timeout'
        || status === 'failed' || status === 'timeout';
    if (!failed) {
        return '';
    }
    return textField(output && output.cause)
        || textField(output && output.error)
        || textField(output && output.reason)
        || routing
        || status
        || 'Recognition failed';
}

function ResultView({ output, rec }) {
    const failure = recognitionFailure(output, rec);
    if (failure) {
        return <pre className="test-runs-json test-runs-result-error">{failure}</pre>;
    }
    return (
        <pre className="test-runs-json">
            {JSON.stringify(clarifiedOf(output) || null, null, 2)}
        </pre>
    );
}

function runChoiceLabel(row) {
    const corpus = row.corpusName ? ` · ${row.corpusName}` : '';
    const status = row.status && row.status !== 'completed' ? ` · ${row.status}` : '';
    return `${formatSuiteLabel(row)}${corpus}${status}`;
}

function shownValue(comparing, base, target, format) {
    if (!comparing) {
        return format(target);
    }
    return formatCompare(format(base), format(target));
}

function MetricReadout({ comparing, base, target }) {
    const baseRules = `${formatMetric(base.lhs)} / ${formatMetric(base.rhs)}`;
    const targetRules = `${formatMetric(target.lhs)} / ${formatMetric(target.rhs)}`;
    const rules = comparing ? formatCompare(baseRules, targetRules) : targetRules;
    return (
        <span className="test-runs-doc-metrics">
            <span>matched {shownValue(comparing, base.matched, target.matched, formatMetric)}</span>
            <span>annotations {shownValue(comparing, base.annotations, target.annotations, formatMetric)}</span>
            <span>LHS / RHS {rules}</span>
            <span>{shownValue(comparing, base.time, target.time, formatSeconds)}</span>
            <span>{shownValue(comparing, base.heap, target.heap, formatBytes)}</span>
        </span>
    );
}

function ProfileBlock({ stack }) {
    if (!stack) {
        return <p>—</p>;
    }
    return <pre className="test-runs-json">{JSON.stringify(stack, null, 2)}</pre>;
}

export default function MailPage({ params, location, router }) {
    const docId = params.docId || params.mailId;
    const compareRaw = location.query && location.query.compare;
    const compareId = typeof compareRaw === 'string' && compareRaw !== params.id ? compareRaw : '';
    const [differencesOnly, setDifferencesOnly] = useState(true);
    const [groupByPhase, setGroupByPhase] = useState(false);
    const [hideSame, setHideSame] = useState(true);

    const { data, error, isLoading } = useQuery(
        ['test-run-mail', params.id, docId],
        () => getSuiteDoc(params.id, docId),
    );
    const runsQuery = useQuery(
        ['test-run-doc-runs', docId],
        () => listRunsForDoc(docId),
    );
    const choices = ((runsQuery.data || []).filter(row => row.id !== params.id));
    const selected = (runsQuery.data || []).find(row => row.id === compareId);
    const baseDocId = (selected && selected.docId) || docId;
    const runsSettled = runsQuery.isSuccess || runsQuery.isError;
    const baseQuery = useQuery(
        ['test-run-mail', compareId, baseDocId],
        () => getSuiteDoc(compareId, baseDocId),
        { enabled: Boolean(compareId) && runsSettled },
    );

    const setCompare = (value) => {
        if (!value) {
            router.push(`/bbr/runs/${params.id}/docs/${docId}`);
            return;
        }
        router.push(`/bbr/runs/${params.id}/docs/${docId}?compare=${encodeURIComponent(value)}`);
    };

    if (error) {
        return <div className="test-runs-page test-runs-error">{error.message}</div>;
    }
    if (isLoading || !data) {
        return <LinearProgress />;
    }

    const comparing = Boolean(compareId && baseQuery.data && !baseQuery.isLoading);
    const targetDoc = data.document || {};
    const baseDoc = (baseQuery.data && baseQuery.data.document) || {};
    const snapshot = {
        sourceMailId: targetDoc.sourceMailId || baseDoc.sourceMailId,
        date: targetDoc.date || baseDoc.date,
        annotationType: targetDoc.annotationType || baseDoc.annotationType,
        subject: targetDoc.subject || baseDoc.subject,
    };
    const targetRecs = recsByType(data);
    const baseRecs = recsByType(comparing ? baseQuery.data : null);
    const types = [];
    Array.from(targetRecs.keys()).forEach((type) => {
        types.push(type);
    });
    Array.from(baseRecs.keys()).forEach((type) => {
        if (types.indexOf(type) === -1) {
            types.push(type);
        }
    });
    if (types.length === 0) {
        types.push(snapshot.annotationType || 'recognition');
    }
    const runLink = compareId
        ? `/bbr/runs/${params.id}?compare=${encodeURIComponent(compareId)}`
        : `/bbr/runs/${params.id}`;
    const primaryType = snapshot.annotationType && targetRecs.has(snapshot.annotationType)
        ? snapshot.annotationType
        : types[0];
    const primaryTarget = targetRecs.get(primaryType);
    const primaryBase = baseRecs.get(primaryType);
    const singleType = types.length === 1;

    return (
        <div className="test-runs-page">
            <div className="test-runs-toolbar">
                <h2>
                    <Link to="/bbr/runs">Recognition tests</Link>
                    {' / '}
                    <Link to={runLink}>{params.id.slice(0, 8)}</Link>
                    {' / '}
                    {docId.slice(0, 8)}
                </h2>
                <div className="test-runs-toolbar-actions">
                    <HoverTip label={runSelectionTip(selected)}>
                        <SelectField
                            floatingLabelText="Compare with"
                            value={compareId || null}
                            onChange={(event, index, value) => setCompare(value)}
                            style={{ width: 320 }}
                        >
                            {choices.map(row => (
                                <MenuItem key={row.id} value={row.id} primaryText={runChoiceLabel(row)} />
                            ))}
                            {compareId && !choices.some(row => row.id === compareId) ? (
                                <MenuItem value={compareId} primaryText={compareId.slice(0, 8)} />
                            ) : null}
                        </SelectField>
                    </HoverTip>
                    {compareId ? (
                        <HoverTip label="Remove">
                            <IconButton onClick={() => setCompare('')}>
                                <NavigationClose />
                            </IconButton>
                        </HoverTip>
                    ) : null}
                </div>
            </div>
            <div className="test-runs-doc-line">
                <DocStatus status={recognitionStatus(primaryTarget || primaryBase)} />
                <span className="test-runs-mail-cell">
                    <HoverTip label={snapshot.subject}>
                        <span>{docId.slice(0, 8)}</span>
                    </HoverTip>
                    <LetterLink sourceMailId={snapshot.sourceMailId} />
                </span>
                <span>{snapshot.annotationType || '—'}</span>
                {snapshot.date ? <span>{snapshot.date}</span> : null}
                {singleType ? (
                    <MetricReadout
                        comparing={comparing}
                        base={statsOf(outputOf(primaryBase))}
                        target={statsOf(outputOf(primaryTarget))}
                    />
                ) : null}
            </div>
            {runsQuery.error ? (
                <div className="test-runs-error">{runsQuery.error.message}</div>
            ) : null}
            {compareId && baseQuery.error ? (
                <div className="test-runs-error">{baseQuery.error.message}</div>
            ) : null}
            {compareId && (baseQuery.isLoading || !baseQuery.data) && !baseQuery.error ? (
                <LinearProgress />
            ) : types.map((type) => {
                const targetRec = targetRecs.get(type);
                const baseRec = baseRecs.get(type);
                const targetOutput = outputOf(targetRec);
                const baseOutput = outputOf(baseRec);
                const targetStats = statsOf(targetOutput);
                const baseStats = statsOf(baseOutput);
                const routing = (targetRec && targetRec.routing) || (baseRec && baseRec.routing) || 'pending';
                return (
                    <div key={type} className="test-runs-mail-block">
                        {singleType ? null : (
                            <div className="test-runs-doc-line">
                                <DocStatus status={recognitionStatus(targetRec || baseRec)} />
                                <span>{type} · {routing}</span>
                                <MetricReadout comparing={comparing} base={baseStats} target={targetStats} />
                            </div>
                        )}
                        <Tabs>
                            <Tab label="Rules">
                                <JapeRulesTable
                                    rows={japeRows(targetOutput)}
                                    baseRows={comparing ? japeRows(baseOutput) : null}
                                    targetRows={comparing ? japeRows(targetOutput) : null}
                                    differencesOnly={differencesOnly}
                                    onDifferencesOnly={setDifferencesOnly}
                                    groupByPhase={groupByPhase}
                                    onGroupByPhase={setGroupByPhase}
                                />
                            </Tab>
                            <Tab label="Result">
                                {comparing && (recognitionFailure(baseOutput, baseRec) || recognitionFailure(targetOutput, targetRec)) ? (
                                    <div className="test-runs-profile-pair">
                                        <div>
                                            <h4>Base</h4>
                                            <ResultView output={baseOutput} rec={baseRec} />
                                        </div>
                                        <div>
                                            <h4>Target</h4>
                                            <ResultView output={targetOutput} rec={targetRec} />
                                        </div>
                                    </div>
                                ) : comparing ? (
                                    <JsonDiff
                                        base={clarifiedOf(baseOutput)}
                                        target={clarifiedOf(targetOutput)}
                                        hideSame={hideSame}
                                        onHideSame={setHideSame}
                                    />
                                ) : (
                                    <ResultView output={targetOutput} rec={targetRec} />
                                )}
                            </Tab>
                            <Tab label="Profiling">
                                {comparing ? (
                                    <div className="test-runs-profile-pair">
                                        <div>
                                            <h4>Base</h4>
                                            <ProfileBlock stack={baseStats.stack} />
                                        </div>
                                        <div>
                                            <h4>Target</h4>
                                            <ProfileBlock stack={targetStats.stack} />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h4>Clarify profile / stack</h4>
                                        <ProfileBlock stack={targetStats.stack} />
                                    </div>
                                )}
                            </Tab>
                        </Tabs>
                    </div>
                );
            })}
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/MailPage.jsx